/* Guarded because some page loads evaluate this file twice; see the note in
   modal-manager.js. bundle-modal.liquid constructs BundleManager, so the class
   is exported onto window rather than left block-scoped. */
if (!window.BundleManager) {

class BundleManager {
  constructor() {
    this.selectedProducts = new Map();
    // History für jede Variante (Größe) speichern
    this.variantHistory = new Map();
    this.currentVariantSize = null;
    this.bundleSelector = document.querySelector('.bundle-selector');
    if (!this.bundleSelector) return;

    // Initialize settings
    this.settings = {
      description_text: this.bundleSelector.dataset.descriptionText || `Wähle %{count} Produkte für deine Geschenkbox.`,
      required: this.bundleSelector.dataset.required !== 'false',
    };

    // Lese die Konfiguration aus den Block-Einstellungen
    this.sizeOptionName = this.bundleSelector.dataset.sizeOptionName || 'Size';
    this.sizeConfigs = this.parseSizeConfigs(this.bundleSelector.dataset.sizeConfigurations || 'S:2,M:3,L:4');

    // Initialize with default values
    this.shopCurrency = document.documentElement.dataset.shopCurrency || 'EUR';
    this.currentPage = 7;
    this.productsPerPage = 10;
    this.isLoading = false;

    // Set initial grid size based on current variant
    const initialVariant = document.querySelector(
      `input[name="${this.sizeOptionName}"]:checked, select[name="${this.sizeOptionName}"]`
    );
    if (initialVariant) {
      // Setze currentVariantSize vor dem ersten Aufruf, damit die History richtig funktioniert
      const initialSize = initialVariant.value || initialVariant.dataset?.optionValue;
      if (initialSize) {
        const matchingSize = Object.keys(this.sizeConfigs).find(
          (configSize) => configSize && configSize.toLowerCase() === initialSize.toLowerCase()
        );
        if (matchingSize) {
          this.currentVariantSize = matchingSize;
        }
      }
      this.updateGridSizeFromVariant(initialVariant);
    } else {
      // Wenn keine initiale Variante gefunden wurde, Widget ausblenden
      this.bundleSelector.style.display = 'none';
    }

    // Nur initialisieren wenn das Widget sichtbar ist
    if (this.bundleSelector.style.display !== 'none') {
      // Initialize grid first
      this.initializeSelectionGrid();

      // Then set up all other listeners and initializations
      this.initializeVariantListener();
      this.initializeModalTriggers();
      this.initializeEventListeners();
      this.initializeProductCards();

      // Add mutation observer to handle dynamically loaded content
      this.setupMutationObserver();

      // Add document-level event delegation for variant selectors
      this.setupVariantEventDelegation();
    }
  }

  parseSizeConfigs(configString) {
    const configs = {};
    if (!configString) return configs;

    configString.split(',').forEach((config) => {
      const [size, count] = config.split(':');
      if (size && count) {
        // Entferne Leerzeichen und behalte die Original-Größe
        configs[size.trim()] = parseInt(count, 10);
      }
    });

    return configs;
  }

  setupMutationObserver() {
    const modalContent = document.querySelector('.modal-content');
    if (!modalContent) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any of the added nodes are product cards or contain product cards
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList?.contains('product-card') || node.querySelector('.product-card')) {
                this.initializeProductCards();
              }
            }
          });
        }
      });
    });

    observer.observe(modalContent, {
      childList: true,
      subtree: true,
    });
  }

  initializeVariantListener() {
    // Listen for variant changes on the main product form
    document.addEventListener('variant:changed', (event) => {
      const variantTitle = event.detail.variant?.title || '';
      this.updateGridSizeFromVariant(event.target);
    });

    // Also listen for change events on Size selector directly
    const sizeInputs = document.querySelectorAll(
      `input[name="${this.sizeOptionName}"], select[name="${this.sizeOptionName}"]`
    );
    sizeInputs.forEach((input) => {
      input.addEventListener('change', (event) => {
        this.updateGridSizeFromVariant(event.target);
      });
    });
  }

  updateGridSizeFromVariant(variant) {
    if (!variant) {
      return;
    }

    const size = variant.value || variant.dataset?.optionValue;
    if (!size) {
      return;
    }

    // Prüfe ob die Größe in den Konfigurationen existiert (case-insensitive)
    const matchingSize = Object.keys(this.sizeConfigs).find(
      (configSize) => configSize && configSize.toLowerCase() === size.toLowerCase()
    );

    if (matchingSize) {
      const newMaxProducts = this.sizeConfigs[matchingSize];
      const previousMaxProducts = this.maxProducts;
      const previousSize = this.currentVariantSize;

      // Speichere die aktuelle Auswahl in der History für die vorherige Variante
      if (previousSize && previousSize !== matchingSize) {
        this.saveVariantHistory(previousSize);
      }

      // Setze neue Variante
      this.maxProducts = newMaxProducts;
      this.currentVariantSize = matchingSize;

      // Wenn wir zu einer größeren Variante wechseln, versuche die History wiederherzustellen
      if (previousMaxProducts && newMaxProducts > previousMaxProducts) {
        this.restoreVariantHistory(matchingSize);
      }

      this.updateGridSize(newMaxProducts);
      this.updateDescriptionText();
      this.updateMaxProductsDisplay();
      
      // Stelle sicher, dass die aktuelle Auswahl nicht größer als die erlaubte Anzahl ist
      // (wichtig wenn keine History vorhanden war oder weniger wiederhergestellt werden konnten)
      this.enforceMaxSelection();
      
      // Grid, Zähler und Preview immer aus dem aktuellen State neu aufbauen
      this.updateGridFromState();
      this.updateSelectedCount();
      this.updateCartPreview();
      // Widget anzeigen
      this.bundleSelector.style.display = '';
      this.validateSelection();
    } else {
      // Widget ausblenden wenn keine passende Größe gefunden wurde
      this.bundleSelector.style.display = 'none';
      // Optional: Ausgewählte Produkte zurücksetzen
      this.resetSelection();
    }
  }

  /**
   * Speichert die aktuelle Auswahl in der History für eine bestimmte Variante.
   */
  saveVariantHistory(variantSize) {
    if (this.selectedProducts.size > 0) {
      // Speichere eine Kopie der aktuellen Auswahl
      const historyCopy = new Map(this.selectedProducts);
      this.variantHistory.set(variantSize, historyCopy);
    }
  }

  /**
   * Stellt die History für eine bestimmte Variante wieder her.
   * Gibt true zurück, wenn eine History wiederhergestellt wurde.
   */
  restoreVariantHistory(variantSize) {
    const history = this.variantHistory.get(variantSize);
    if (history && history.size > 0) {
      // Stelle die History wieder her, aber begrenze auf maxProducts
      const historyArray = Array.from(history.entries());
      const productsToRestore = historyArray.slice(0, this.maxProducts);
      
      // Setze die ausgewählten Produkte
      this.selectedProducts.clear();
      productsToRestore.forEach(([productId, productData]) => {
        this.selectedProducts.set(productId, productData);
        
        // UI-Status der entsprechenden Produktkarte aktualisieren
        const productCard = document.querySelector(`.product-card[data-product-id="${productId}"]`);
        if (productCard) {
          this.updateProductVisualState(productCard, true);
        }
      });

      // UI aktualisieren
      this.updateModalState();
      return true;
    }
    return false;
  }

  /**
   * Stellt sicher, dass die Anzahl der ausgewählten Produkte nicht größer als this.maxProducts ist.
   * Wenn zu viele Produkte ausgewählt sind, werden die zuletzt hinzugefügten entfernt.
   * Speichert die entfernten Produkte in der History, falls noch nicht gespeichert.
   */
  enforceMaxSelection() {
    if (this.selectedProducts.size <= this.maxProducts) return;

    // Speichere die aktuelle Auswahl in der History bevor wir Produkte entfernen
    if (this.currentVariantSize) {
      this.saveVariantHistory(this.currentVariantSize);
    }

    const overflow = this.selectedProducts.size - this.maxProducts;
    const productIds = Array.from(this.selectedProducts.keys());

    // Entferne die zuletzt hinzugefügten Produkte (am Ende der Map)
    for (let i = 0; i < overflow; i++) {
      const productId = productIds[productIds.length - 1 - i];
      this.selectedProducts.delete(productId);

      // UI-Status der entsprechenden Produktkarte zurücksetzen
      const productCard = document.querySelector(`.product-card[data-product-id="${productId}"]`);
      if (productCard) {
        this.updateProductVisualState(productCard, false);
      }
    }

    // UI entsprechend der neuen Auswahl aktualisieren
    this.updateModalState();
    this.updateGridFromState();
    this.updateSelectedCount();
    this.updateCartPreview();
  }

  updateGridSize(size) {
    const grid = this.bundleSelector.querySelector('#bundleSelectionGrid');
    if (!grid) return;

    // Clear existing slots
    grid.innerHTML = '';

    // Add new slots based on size
    for (let i = 0; i < size; i++) {
      this.addEmptySlot();
    }

    // Update grid columns based on size
    grid.classList.remove('grid-cols-1', 'grid-cols-2', 'grid-cols-3', 'grid-cols-4');

    if (size === 1) {
      grid.classList.add('grid-cols-1');
    } else if (size === 2) {
      grid.classList.add('grid-cols-2');
    } else if (size === 3) {
      grid.classList.add('grid-cols-3');
    } else {
      // Bei 4 oder mehr Produkten immer 2 nebeneinander
      grid.classList.add('grid-cols-2');
    }
  }

  updateDescriptionText() {
    const descriptionElement = document.querySelector('.bundle-selector p');
    if (descriptionElement) {
      const descriptionText =
        this.settings.description_text || `Wähle ${this.maxProducts} Produkte für deine Geschenkbox.`;
      descriptionElement.textContent = descriptionText.replace('%{count}', this.maxProducts);
    }
  }

  initializeModalTriggers() {
    // Add click event listeners to all elements that should open the modal
    document.querySelectorAll('[onclick*="bundleManager.openModal()"]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openModal();
      });
    });

    // Also add listeners to empty slots
    document.querySelectorAll('.bundle-slot').forEach((slot) => {
      slot.addEventListener('click', () => {
        this.openModal();
      });
    });
  }

  async loadProductDetails(sku) {
    try {
      const response = await fetch(`/products/${sku}?view=json`);
      const productData = await response.json();
      return {
        title: productData.title,
        price: productData.price,
        image: productData.featured_image,
        variants: productData.variants,
        sku: productData.sku,
      };
    } catch (error) {
      console.error('Error loading product details:', error);
      return null;
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      style: 'currency',
      currency: this.shopCurrency,
    }).format(cents / 100);
  }

  updateProductVisualState(productCard, isSelected) {
    const selectButton = productCard.querySelector('.bundle-product-select-button');

    // Update data attribute and class
    productCard.dataset.selected = isSelected ? 'true' : 'false';
    productCard.classList.toggle('selected', isSelected);

    // Update button if it exists
    if (selectButton) {
      if (isSelected) {
        selectButton.textContent = 'Ausgewählt';
        selectButton.classList.remove('bg-white', 'text-black');
        selectButton.classList.add('bg-black', 'text-white');
      } else {
        selectButton.textContent = 'Auswählen';
        selectButton.classList.remove('bg-black', 'text-white');
        selectButton.classList.add('bg-white', 'text-black');
      }
    }
  }

  initializeProductCards() {
    const productCards = document.querySelectorAll('.product-card');

    productCards.forEach((productCard, index) => {
      // Initial state
      const productId = productCard.dataset.productId;
      const isSelected = this.selectedProducts.has(productId);

      this.updateProductVisualState(productCard, isSelected);

      // Handle product selection
      const selectButton = productCard.querySelector('.bundle-product-select-button');
      if (selectButton) {
        // Remove any existing listeners to prevent duplicates
        selectButton.replaceWith(selectButton.cloneNode(true));
        const newSelectButton = productCard.querySelector('.bundle-product-select-button');

        newSelectButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Get all necessary data from the product card
          const productData = {
            id: productCard.dataset.productId,
            variantId: productCard.dataset.variantId,
            price: parseFloat(productCard.dataset.productPrice),
            title: productCard.dataset.productTitle,
            imageUrl: productCard.dataset.variantImage,
            hoverImageUrl: productCard.dataset.variantHoverImage,
            quantity: 1,
            currency: productCard.dataset.productCurrency,
            variantTitle: productCard.dataset.variantTitle,
            sku: productCard.dataset.sku,
            comparePrice: productCard.dataset.comparePrice,
            selectedColor: productCard.dataset.selectedColor,
          };

          // Dispatch event for other components to handle the selection
          const event = new CustomEvent('bundle:product:selected', {
            detail: productData,
          });
          document.dispatchEvent(event);

          // Update selected state
          productCard.dataset.selected = 'true';

          // Call the selectProduct method
          this.selectProduct(productCard);
        });
      }
    });
  }

  initializeEventListeners() {
    // Initialize product type filters for both desktop and mobile
    const filterButtons = document.querySelectorAll('.product-type-filter, .product-type-filter-mobile');
    filterButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Get the appropriate container based on button class
        const isMobile = button.classList.contains('product-type-filter-mobile');
        const container = isMobile
          ? document.getElementById('bottomSheet')
          : document.getElementById('bundleProductModal');

        // Update active state for buttons in the same container
        container.querySelectorAll(isMobile ? '.product-type-filter-mobile' : '.product-type-filter').forEach((btn) => {
          btn.classList.remove('active');
        });
        button.classList.add('active');

        // Filter products
        const type = button.dataset.type;
        const products = container.querySelectorAll(isMobile ? '.product-grid-item-mobile' : '.product-grid-item');

        products.forEach((product) => {
          if (type === 'all' || product.dataset.productType === type) {
            product.style.display = '';
          } else {
            product.style.display = 'none';
          }
        });
      });
    });

    // Add click handlers for modal buttons
    const closeButton = document.querySelector('[data-modal-close]');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.closeModal());
    }

    const confirmButton = document.querySelector('[data-modal-confirm]');
    if (confirmButton) {
      confirmButton.addEventListener('click', () => this.confirmSelection());
    }

    const cancelButton = document.querySelector('[data-modal-cancel]');
    if (cancelButton) {
      cancelButton.addEventListener('click', () => this.cancelSelection());
    }

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const [url, options] = args;

      /* fetch also accepts a URL object or a Request, and callers in this
         theme pass URL objects. Calling .includes() on those threw and took
         the whole request down, so normalise before matching. */
      const requestUrl =
        typeof url === 'string' ? url : url instanceof Request ? url.url : String(url ?? '');

      // Intercept cart section updates
      if (requestUrl.includes('/cart?section_id=')) {
        // Wenn wir gerade ein Bundle hinzufügen, warten wir bis alles fertig ist
        if (window.isAddingBundle) {
          // Warten bis die Bundle-Produkte hinzugefügt wurden
          await new Promise((resolve) => setTimeout(resolve, 500));
          // Sections neu laden mit allen notwendigen Abschnitten
          const sections = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items'];
          const sectionsResponse = await fetch(`?sections=${sections.join(',')}`);
          const sectionsJson = await sectionsResponse.json();

          // Cart Drawer updaten
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer?.renderContents) {
            cartDrawer.renderContents({
              sections: sectionsJson,
            });
          }

          window.isAddingBundle = false;
          return sectionsResponse;
        }
      }

      // Check if this is an add to cart request
      if (requestUrl.includes('/cart/add') && options?.body) {
        // Only intercept if we have bundle products and it's not already a bundle request
        if (this.selectedProducts.size > 0 && !requestUrl.includes('items%5B%5D')) {
          try {
            window.isAddingBundle = true;

            // Get original body data
            const originalBody =
              options.body instanceof FormData ? Object.fromEntries(options.body.entries()) : JSON.parse(options.body);

            // Calculate total price from selected products only
            const selectedProductsTotal = Array.from(this.selectedProducts.values()).reduce((total, product) => {
              return total + (product.price || 0);
            }, 0);

            // Create SKU array from selected products
            const bundledSkus = Array.from(this.selectedProducts.values())
              .map((product) => product.sku)
              .filter((sku) => sku);

            // Create new request body with modified price
            const newBody = {
              ...originalBody,
              price: selectedProductsTotal, // Setze den Preis direkt
              properties: {
                Artikel: bundledSkus.join(', '),
              },
            };

            // Send modified request
            const response = await originalFetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify(newBody),
            });

            if (response.ok) {
              const sections = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items'];
              const sectionsUrl = `?sections=${sections.join(',')}`;
              const sectionsResponse = await fetch(sectionsUrl);
              const sectionsJson = await sectionsResponse.json();

              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer?.renderContents) {
                cartDrawer.renderContents({
                  sections: sectionsJson,
                });
              }

              this.resetSelection(true); // Clear all history when adding to cart
              window.isAddingBundle = false;
            }

            return response;
          } catch (error) {
            console.error('Error in fetch intercept:', error);
            window.isAddingBundle = false;
            return originalFetch(...args);
          }
        }
      }

      // For all other requests, proceed normally
      return originalFetch(...args);
    };

    document.addEventListener('cart:refresh', () => {
      this.resetSelection(true); // Clear all history when cart is refreshed
    });

    document.addEventListener('variant:changed', (event) => {
      const productElement = event.target;
      const productId = productElement.dataset.productId;

      // Update product card data attributes
      const { variantId, price, comparePrice, image, hoverImage, variantTitle, sku, selectedColor } = event.detail;

      // Update all data attributes on the product card
      productElement.dataset.variantId = variantId;
      productElement.dataset.productPrice = price;
      productElement.dataset.variantImage = image;
      productElement.dataset.variantHoverImage = hoverImage;
      productElement.dataset.variantTitle = variantTitle;
      productElement.dataset.sku = sku;
      productElement.dataset.comparePrice = comparePrice;
      if (selectedColor) {
        productElement.dataset.selectedColor = selectedColor;
      }

      // Update main image
      const mainImage = productElement.querySelector('.product-main-image');
      if (mainImage) {
        mainImage.src = image;
      }

      // Update hover image if it exists
      const hoverImageContainer = productElement.querySelector('.product-main-image').parentElement;
      if (hoverImageContainer) {
        const existingHoverImage = hoverImageContainer.querySelector('.product-main-image + img');
        if (existingHoverImage) {
          existingHoverImage.src = hoverImage;
        }
      }

      // Update price display
      const priceElement = productElement.querySelector('.price');
      const comparePriceElement = productElement.querySelector('.compare-price');

      if (priceElement) {
        const formattedPrice = new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR',
        }).format(price / 100);
        priceElement.textContent = formattedPrice;

        if (comparePrice > 0) {
          priceElement.classList.add('text-[#E32C2B]');
          priceElement.classList.remove('text-[#121212]');
        } else {
          priceElement.classList.remove('text-[#E32C2B]');
          priceElement.classList.add('text-[#121212]');
        }
      }

      if (comparePriceElement) {
        if (comparePrice > 0) {
          const formattedComparePrice = new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
          }).format(comparePrice / 100);
          comparePriceElement.textContent = formattedComparePrice;
          comparePriceElement.classList.remove('hidden');
        } else {
          comparePriceElement.classList.add('hidden');
        }
      }

      if (this.selectedProducts.has(productId)) {
        const updatedProductData = {
          id: productId,
          variantId: variantId,
          price: parseFloat(price),
          title: productElement.dataset.productTitle,
          imageUrl: image,
          hoverImageUrl: hoverImage,
          quantity: 1,
          currency: this.shopCurrency,
          variantTitle: variantTitle || '',
          sku: sku,
        };

        this.selectedProducts.set(productId, updatedProductData);
        this.updateGridFromState();
        this.updateCartPreview();
      }
    });
  }

  async addBundleProductsToCart() {
    if (this.selectedProducts.size === 0) return;

    const currentProductHandle = window.location.pathname.split('/products/')[1]?.split('?')[0] || '';
    const uniqueId = Math.floor(1000 + Math.random() * 9000);

    const items = Array.from(this.selectedProducts.values()).map((product) => ({
      id: parseInt(product.variantId),
      quantity: product.quantity || 1,
      properties: {
        Box: `${currentProductHandle}_${uniqueId}`,
      },
    }));

    const response = await fetch(window.Shopify?.routes?.root + 'cart/add.js' || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const responseData = await response.json();
      throw new Error(responseData.description || 'Failed to add bundle products');
    }

    // Reset selection after successful addition
    this.resetSelection(true); // Clear all history when adding to cart

    return response.json();
  }

  initializeSelectionGrid() {
    const grid = document.getElementById('bundleSelectionGrid');
    if (!grid) return;

    // Clear existing grid
    grid.innerHTML = '';

    // Add appropriate number of slots based on maxProducts
    for (let i = 0; i < this.maxProducts; i++) {
      this.addEmptySlot();
    }
  }

  addEmptySlot() {
    const grid = document.getElementById('bundleSelectionGrid');
    if (!grid) return;

    const slot = document.createElement('div');
    slot.className = 'bundle-slot card-wrapper product-card-wrapper underline-links-hover';
    slot.innerHTML = this.generateSlotHTML();
    grid.appendChild(slot);
  }

  openModal() {
    const modal = document.getElementById('bundleProductModal');
    const bottomSheet = document.getElementById('bottomSheet');

    if (modal) {
      modal.classList.remove('hidden');
      requestAnimationFrame(() => {
        modal.classList.add('active');
        if (bottomSheet) {
          bottomSheet.style.transform = 'translateY(0)';
        }
      });

      // Add click handler for closing modal when clicking outside
      const handleOutsideClick = (event) => {
        const modalContent = modal.querySelector('.modal-content');
        if (modalContent && !modalContent.contains(event.target)) {
          this.closeModal();
          modal.removeEventListener('click', handleOutsideClick);
        }
      };

      modal.addEventListener('click', handleOutsideClick);

      // Update the selected count and max count when modal opens
      this.updateSelectedCount();
    }

    document.body.style.overflow = 'hidden';
    this.updateModalState();
  }

  closeModal() {
    const modal = document.getElementById('bundleProductModal');
    const bottomSheet = document.getElementById('bottomSheet');

    if (modal) {
      modal.classList.remove('active');
      if (bottomSheet) {
        bottomSheet.style.transform = 'translateY(100%)';
      }

      // Wait for transition to complete before hiding
      setTimeout(() => {
        modal.classList.add('hidden');
        document.body.style.overflow = '';
      }, 300);
    }
  }

  selectProduct(productElement) {
    // Stelle sicher, dass wir das richtige Element haben
    const productCard = productElement.closest('.product-card');
    if (!productCard) {
      console.error('Kein Produkt-Card Element gefunden');
      return;
    }

    const productId = productCard.dataset.productId;
    const variantId = productCard.dataset.variantId;

    // Hole die aktuellen Daten aus den data-Attributen
    const productData = {
      id: productId,
      variantId: variantId,
      price: parseInt(productCard.dataset.productPrice, 10),
      title: productCard.dataset.productTitle || '',
      imageUrl: productCard.dataset.variantImage || '',
      hoverImageUrl: productCard.dataset.variantHoverImage || '',
      quantity: 1,
      currency: this.shopCurrency,
      variantTitle: productCard.dataset.variantTitle || '',
      sku: productCard.dataset.sku || '',
    };

    // Wenn das Produkt bereits ausgewählt ist, entferne es
    if (this.selectedProducts.has(productId)) {
      this.selectedProducts.delete(productId);
      this.updateProductVisualState(productCard, false);
      this.updateModalState();
      this.updateGridFromState();
      this.validateSelection();
      this.updateSelectedCount();
      this.updateCartPreview();
      return;
    }

    // Ab hier nur noch Logik für das Auswählen eines neuen Produkts
    if (!variantId) {
      console.error('Keine Variante ausgewählt');
      return;
    }

    // Check if we've reached the maximum products
    if (this.selectedProducts.size >= this.maxProducts) {
      alert(`Du kannst maximal ${this.maxProducts} Produkte für diese Größe auswählen.`);
      return;
    }

    // Validiere die Daten
    if (!productData.title || !productData.imageUrl || isNaN(productData.price)) {
      console.error('Ungültige Produktdaten:', productData);
      return;
    }

    // Füge das Produkt hinzu
    this.selectedProducts.set(productId, productData);

    // Update UI
    this.updateProductVisualState(productCard, true);
    this.updateModalState();
    this.updateGridFromState();
    this.validateSelection();
    this.updateSelectedCount();
    this.updateCartPreview();
  }

  updateModalState() {
    const modal = document.getElementById('bundleProductModal');
    if (!modal) return;

    const productElements = modal.querySelectorAll('.product-card[data-product-id]');

    productElements.forEach((element) => {
      const productId = element.dataset.productId;
      const isSelected = this.selectedProducts.has(productId);
      this.updateProductVisualState(element, isSelected);
    });
  }

  updateGridFromState() {
    const grid = document.getElementById('bundleSelectionGrid');
    if (!grid) return;

    // Check if prices should be hidden
    const bundleSelector = document.querySelector('.bundle-selector');
    const hidePrices = bundleSelector ? bundleSelector.dataset.hidePrices === 'true' : false;
    const priceVisibilityClass = hidePrices ? 'invisible' : '';

    // Clear the grid
    grid.innerHTML = '';

    // Add selected products
    Array.from(this.selectedProducts.values()).forEach((productData) => {
      const slot = document.createElement('div');
      slot.className = 'bundle-slot card-wrapper product-card-wrapper underline-links-hover';

      // Erstelle den Anzeigenamen
      const displayTitle = productData.title;
      const displayVariant = productData.variantTitle
        ? `<p class="text-sm text-gray-600 mt-1">${productData.variantTitle}</p>`
        : '';

      slot.innerHTML = `
        <div class="card card--standard card--media" style="--ratio-percent: 100%">
          <div class="card__inner color-background-2 gradient ratio mb-[1.6rem]" style="--ratio-percent: 100%">
            <div class="card__media">
              <div class="media media--transparent media--hover-effect !static">
                <img 
                  src="${productData.imageUrl}"
                  alt="${displayTitle}"
                  class="motion-reduce object-contain w-full h-full"
                  loading="lazy"
                  style="--ratio-percent: 100%"
                >
              </div>
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h5">
                <span class="full-unstyled-link">${displayTitle}</span>
              </h3>
              ${displayVariant}
              <div class="card-information">
                <div class="price ${priceVisibilityClass}">
                  <div class="price__container">
                    <div class="price__regular">
                      <span class="price-item price-item--regular">${this.formatMoney(productData.price)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="quick-add no-js-hidden w-full mt-5">
              <button type="button" 
                class="quick-add__submit button button--full-width button--secondary"
                onclick="bundleManager.removeProduct('${productData.id}')"
              >
                Entfernen
              </button>
            </div>
          </div>
        </div>
      `;
      grid.appendChild(slot);
    });

    // Add empty slots for remaining spaces
    const remainingSlots = this.maxProducts - this.selectedProducts.size;
    for (let i = 0; i < remainingSlots; i++) {
      this.addEmptySlot();
    }
  }

  removeProduct(productId) {
    // Remove from selected products
    this.selectedProducts.delete(productId);

    // Find the product card in the modal
    const modalProduct = document.querySelector(`#bundleProductModal [data-product-id="${productId}"]`);

    if (modalProduct) {
      const productDataScript = document.getElementById(`productData-${productId}`);

      if (productDataScript) {
        const productData = JSON.parse(productDataScript.textContent);

        // Get first available variant
        const firstVariant = productData.variants.find((v) => v.available) || productData.variants[0];

        if (firstVariant) {
          // Reset variant selector button text
          const variantSelectorBtn = modalProduct.querySelector('.variant-selector-btn');
          if (variantSelectorBtn) {
            variantSelectorBtn.textContent = 'Variante wählen';
          }

          // Reset color selection to first variant's color
          const colorOptions = modalProduct.querySelectorAll('.color-variant-btn');
          colorOptions.forEach((option) => {
            const isFirstVariantColor = option.dataset.optionValue === firstVariant.options[0];
            option.querySelector('span').dataset.selected = isFirstVariantColor;
            if (isFirstVariantColor) {
              option.dataset.default = 'true';
            } else {
              option.dataset.default = 'false';
            }
          });

          // Reset variant options to match first variant
          const variantOptions = modalProduct.querySelectorAll('.variant-option-btn');
          variantOptions.forEach((option) => {
            const optionName = option.dataset.optionName;
            const optionValue = option.dataset.optionValue;
            const isFirstVariantOption =
              firstVariant.options[productData.options.findIndex((opt) => opt.name === optionName)] === optionValue;
            option.setAttribute('data-selected', isFirstVariantOption);
            if (isFirstVariantOption) {
              option.classList.add('border-black', 'bg-black', 'text-white');
              option.classList.remove('border-gray-200', 'bg-white', 'text-black');
            } else {
              option.classList.remove('border-black', 'bg-black', 'text-white');
              option.classList.add('border-gray-200', 'bg-white', 'text-black');
            }
          });

          // Update product card data attributes
          modalProduct.dataset.variantId = firstVariant.id;
          modalProduct.dataset.productPrice = firstVariant.price;
          modalProduct.dataset.variantTitle = firstVariant.options.join(' / ');
          modalProduct.dataset.sku = firstVariant.sku || '';

          // Get the initial variant's image URL from the color button
          const colorBtn = modalProduct.querySelector(
            `.color-variant-btn[data-option-value="${firstVariant.options[0]}"]`
          );

          // Get main image from data-variant-image
          const mainImageUrl = colorBtn?.dataset.variantImage || '';

          if (mainImageUrl) {
            modalProduct.dataset.productImage = mainImageUrl;

            // Update main image
            const mainImage = modalProduct.querySelector('.product-main-image');
            if (mainImage) {
              mainImage.src = mainImageUrl;
              mainImage.setAttribute('src', mainImageUrl);
            }

            // Remove any existing hover image and ensure container is visible
            const hoverImageContainer = modalProduct.querySelector('.product-main-image').parentElement;
            if (hoverImageContainer) {
              const existingHoverImage = hoverImageContainer.querySelector('.product-main-image + img');
              if (existingHoverImage) {
                existingHoverImage.remove();
              }
              // Remove opacity-0 to make the container visible
              hoverImageContainer.classList.remove('opacity-0');
            }
          }

          // Update price display
          const priceElement = modalProduct.querySelector('.price');
          const comparePriceElement = modalProduct.querySelector('.compare-price');

          if (priceElement) {
            const formattedPrice = new Intl.NumberFormat('de-DE', {
              style: 'currency',
              currency: 'EUR',
            }).format(firstVariant.price / 100);
            priceElement.textContent = formattedPrice;

            if (firstVariant.compare_at_price > 0) {
              priceElement.classList.add('text-[#E32C2B]');
              priceElement.classList.remove('text-[#121212]');
            } else {
              priceElement.classList.remove('text-[#E32C2B]');
              priceElement.classList.add('text-[#121212]');
            }
          }

          if (comparePriceElement) {
            if (firstVariant.compare_at_price > 0) {
              const formattedComparePrice = new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
              }).format(firstVariant.compare_at_price / 100);
              comparePriceElement.textContent = formattedComparePrice;
              comparePriceElement.classList.remove('hidden');
            } else {
              comparePriceElement.classList.add('hidden');
            }
          }

          // Reinitialize the variant selector to ensure proper image handling
          const variantContainer = modalProduct.querySelector('.product-variants');
          if (variantContainer) {
            // Remove existing variant selector instance if it exists
            if (variantContainer.variantSelector) {
              delete variantContainer.variantSelector;
            }
            // Assign the new instance to the container property
            variantContainer.variantSelector = new VariantSelector(variantContainer);
          }
        }
      }
    }

    // Update the rest of the UI
    this.updateModalState();
    this.updateGridFromState();
    this.validateSelection();
    this.updateSelectedCount();
    this.updateCartPreview();
  }

  updateSelectedCount() {
    // Update all elements with selectedCount ID
    document.querySelectorAll('#selectedCount').forEach((element) => {
      element.textContent = this.selectedProducts.size;
    });

    const maxCountElement = document.getElementById('maxProductCount');
    const maxCountElementMobile = document.getElementById('maxProductCountMobile');
    const modalElement = document.getElementById('bundleProductModal');
    const bottomSheet = document.getElementById('bottomSheet');

    if (maxCountElement) {
      maxCountElement.textContent = this.maxProducts;
    }
    if (maxCountElementMobile) {
      maxCountElementMobile.textContent = this.maxProducts;
    }

    // Update CSS variables on root element for both modals
    document.documentElement.style.setProperty('--selected-count', this.selectedProducts.size);
    document.documentElement.style.setProperty('--max-products', this.maxProducts);

    // Also update on individual modals for redundancy
    if (modalElement) {
      modalElement.style.setProperty('--selected-count', this.selectedProducts.size);
      modalElement.style.setProperty('--max-products', this.maxProducts);
    }
    if (bottomSheet) {
      bottomSheet.style.setProperty('--selected-count', this.selectedProducts.size);
      bottomSheet.style.setProperty('--max-products', this.maxProducts);
    }
  }

  validateSelection() {
    const errorElement = document.getElementById('bundleError');
    const addToCartButton = document.querySelector('product-form button[name="add"]');

    if (this.bundleSelector.style.display === 'none') {
      if (errorElement) {
        errorElement.textContent = '';
      }
      if (addToCartButton) {
        addToCartButton.disabled = false;
      }
      return;
    }

    if (this.settings.required && this.selectedProducts.size < this.maxProducts) {
      if (errorElement) {
        errorElement.innerHTML = `<span class="font-roboto">Bitte wähle ${this.maxProducts} Produkte aus.</span>`;
      }
      if (addToCartButton) {
        addToCartButton.disabled = true;
      }
    } else if (this.selectedProducts.size > this.maxProducts) {
      if (errorElement) {
        errorElement.innerHTML = `<span class="font-roboto">Du kannst maximal ${this.maxProducts} Produkte auswählen.</span>`;
      }
      if (addToCartButton) {
        addToCartButton.disabled = true;
      }
    } else {
      if (errorElement) {
        errorElement.textContent = '';
      }
      if (addToCartButton) {
        addToCartButton.disabled = false;
      }
    }
  }

  updateCartPreview() {
    const preview = document.getElementById('bundleCartPreview');
    const itemsContainer = document.getElementById('bundlePreviewItems');
    const totalElement = document.getElementById('bundleTotal');
    const mainProductPriceElement = document.querySelector('.product__info-wrapper .price__regular .price-item');

    if (!preview || !itemsContainer || !totalElement) return;

    if (this.selectedProducts.size === 0) {
      preview.classList.add('hidden');
      if (mainProductPriceElement) {
        // Reset to original price
        mainProductPriceElement.textContent = '0,00 €'; // Starte bei 0
      }
      return;
    }

    preview.classList.remove('hidden');
    itemsContainer.innerHTML = '';
    let totalCents = 0;

    this.selectedProducts.forEach((product) => {
      const quantity = product.quantity || 1;
      totalCents += product.price * quantity;

      // Add product to preview
      const itemElement = document.createElement('div');
      itemElement.className = 'flex items-center justify-between space-x-2';
      itemElement.innerHTML = `
      <span class="text-sm truncate">
        ${product.title}
        ${product.variantTitle ? `<br><span class="text-gray-600">${product.variantTitle}</span>` : ''} 
        × ${quantity}
      </span>
      <span class="text-sm font-medium">${this.formatMoney(product.price * quantity)}</span>
    `;

      itemsContainer.appendChild(itemElement);
    });

    // Update total price display - nur die ausgewählten Produkte
    totalElement.textContent = this.formatMoney(totalCents);

    // Update main product price - nur die ausgewählten Produkte
    if (mainProductPriceElement) {
      mainProductPriceElement.textContent = this.formatMoney(totalCents);
    }
  }

  async loadMoreProducts() {
    if (this.isLoading) {
      return;
    }

    const loadMoreButton = document.getElementById('loadMoreButton');
    const loadMoreButtonMobile = document.getElementById('loadMoreButtonMobile');
    const productGrid = document.querySelector('.modal-content .product-grid');
    const productGridMobile = document.querySelector('#mobileModalContent .product-grid');

    if (!productGrid && !productGridMobile) return;

    this.isLoading = true;
    if (loadMoreButton) loadMoreButton.textContent = 'Lädt...';
    if (loadMoreButtonMobile) loadMoreButtonMobile.textContent = 'Lädt...';

    try {
      const collectionHandle = (productGrid || productGridMobile).closest('.grid-container').dataset.collectionHandle;
      const currentFilter =
        document.querySelector('.product-type-filter.active, .product-type-filter-mobile.active')?.dataset.type ||
        'all';

      // Add page parameter to URL
      const url = new URL(`/collections/${collectionHandle}`, window.location.origin);
      url.searchParams.set('view', 'bundle-ajax');
      url.searchParams.set('page', this.currentPage + 1);
      url.searchParams.set('limit', '10'); // Set limit to 10 products per page

      const response = await fetch(url.toString());
      const html = await response.text();

      if (html.trim()) {
        this.currentPage++;

        // Create temporary container
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Filter products if needed
        if (currentFilter !== 'all') {
          const items = tempDiv.querySelectorAll('.product-grid-item, .product-grid-item-mobile');
          items.forEach((item) => {
            if (item.dataset.productType !== currentFilter) {
              item.remove();
            }
          });
        }

        // Get all product items and append them one by one
        const newProducts = tempDiv.querySelectorAll('.product-grid-item, .product-grid-item-mobile');

        if (newProducts.length > 0) {
          // Create a fragment to hold all new products
          const fragment = document.createDocumentFragment();
          newProducts.forEach((product) => {
            // Mark as newly loaded
            product.dataset.newlyLoaded = 'true';
            fragment.appendChild(product);
          });

          // Append all products at once to both grids if they exist
          if (productGrid) productGrid.appendChild(fragment.cloneNode(true));
          if (productGridMobile) productGridMobile.appendChild(fragment.cloneNode(true));

          // Update product type counts
          this.updateProductTypeCounts();

          // Initialize new product cards
          this.initializeProductCards();

          // Initialize variant functionality for newly loaded products
          const newlyLoadedProducts = document.querySelectorAll(
            '.product-grid-item[data-newly-loaded="true"], .product-grid-item-mobile[data-newly-loaded="true"]'
          );
          newlyLoadedProducts.forEach((product) => {
            // Initialize variant selector
            const variantContainer = product.querySelector('.product-variants');
            if (variantContainer) {
              // Remove any existing variant selector instance
              if (variantContainer.variantSelector) {
                delete variantContainer.variantSelector;
              }
              // Create new variant selector instance
              variantContainer.variantSelector = new VariantSelector(variantContainer);
            }

            // Add click handlers for variant selector buttons
            // Note: We use the VariantSelector instance method instead of manual toggle
            // The VariantSelector class already handles the dropdown toggle and event listeners
            // The event delegation in setupVariantEventDelegation() should handle this,
            // but we ensure the VariantSelector instance is properly initialized

            // Add click handlers for color swatches
            const colorButtons = product.querySelectorAll('.color-variant-btn');
            colorButtons.forEach((btn) => {
              btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const variantSelector = btn.closest('.product-variants')?.variantSelector;
                if (variantSelector) {
                  variantSelector.handleColorVariantClick(e);
                }
              });
            });

            // Add click handlers for variant options
            const variantOptions = product.querySelectorAll('.variant-option-btn');
            variantOptions.forEach((btn) => {
              btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const variantSelector = btn.closest('.product-variants')?.variantSelector;
                if (variantSelector) {
                  variantSelector.handleVariantOptionClick(e);
                }
              });
            });
          });
        }
      }
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      this.isLoading = false;
      if (loadMoreButton) loadMoreButton.textContent = 'Mehr Produkte laden';
      if (loadMoreButtonMobile) loadMoreButtonMobile.textContent = 'Mehr Produkte laden';
    }
  }

  updateProductTypeCounts() {
    // Get all product type filters container
    const filterContainer = document.querySelector('.product-type-filter').parentElement;

    // Get all products currently in the grid
    const products = document.querySelectorAll('.product-grid-item');

    // Count products by type and track all types
    const typeCounts = new Map();
    const existingTypes = new Set();
    let totalCount = 0;

    // Get existing filter types
    document.querySelectorAll('.product-type-filter').forEach((button) => {
      const type = button.dataset.type;
      if (type !== 'all') {
        existingTypes.add(type);
      }
    });

    // Count products and collect new types
    products.forEach((product) => {
      const type = product.dataset.productType;
      totalCount++;
      typeCounts.set(type, (typeCounts.get(type) || 0) + 1);

      // If this is a new type, create a filter button for it
      if (!existingTypes.has(type) && type !== 'all') {
        const newButton = document.createElement('button');
        newButton.className =
          'product-type-filter whitespace-nowrap px-4 py-2 rounded-full text-[14px] border border-gray-200 hover:bg-black/5 transition-colors duration-200';
        newButton.dataset.type = type;
        newButton.textContent = `${type} (0)`; // Initial count will be updated below

        // Add click handler
        newButton.addEventListener('click', () => {
          // Update active state
          document.querySelectorAll('.product-type-filter').forEach((btn) => btn.classList.remove('active'));
          newButton.classList.add('active');

          // Filter products
          document.querySelectorAll('.product-grid-item').forEach((product) => {
            if (type === 'all' || product.dataset.productType === type) {
              product.style.display = '';
            } else {
              product.style.display = 'none';
            }
          });
        });

        filterContainer.appendChild(newButton);
        existingTypes.add(type);
      }
    });

    // Update all filter button counts
    document.querySelectorAll('.product-type-filter').forEach((button) => {
      const type = button.dataset.type;
      if (type === 'all') {
        button.textContent = `Alle Produkte (${totalCount})`;
      } else {
        const count = typeCounts.get(type) || 0;
        const typeText = button.textContent.split('(')[0].trim();
        button.textContent = `${typeText} (${count})`;
      }
    });
  }

  resetSelection(clearAllHistory = false) {
    this.selectedProducts.clear();
    
    // Wenn clearAllHistory true ist (z.B. nach Hinzufügen zum Warenkorb), lösche die gesamte History
    if (clearAllHistory) {
      this.variantHistory.clear();
    } else {
      // Ansonsten lösche nur die History für die aktuelle Variante
      if (this.currentVariantSize) {
        this.variantHistory.delete(this.currentVariantSize);
      }
    }
    
    const grid = document.getElementById('bundleSelectionGrid');
    if (grid) {
      grid.innerHTML = '';
      for (let i = 0; i < this.maxProducts; i++) {
        this.addEmptySlot();
      }
    }

    this.updateModalState();
    this.validateSelection();
    this.updateSelectedCount();
    this.updateCartPreview();
  }

  confirmSelection() {
    // Store current selection
    const currentSelection = new Map(this.selectedProducts);

    // Close the modal
    this.closeModal();

    // Restore selection
    this.selectedProducts = currentSelection;

    // Update the grid state
    this.updateGridFromState();
    this.validateSelection();
    this.updateSelectedCount();
    this.updateCartPreview();
  }

  cancelSelection() {
    // Reset all selections
    this.selectedProducts.clear();

    // Update UI
    this.updateModalState();
    this.updateGridFromState();
    this.validateSelection();
    this.updateSelectedCount();
    this.updateCartPreview();

    // Close the modal
    this.closeModal();
  }

  initializeVariantFunctionality(container) {
    // This method is now handled by bundle-variant-selector.js
    return;
  }

  updateAvailableOptions(productCard, availableOptions, selectedOptions) {
    // This method is now handled by bundle-variant-selector.js
    return;
  }

  updateProductCardWithVariant(productCard, variant, productData, selectedOptions) {
    // This method is now handled by bundle-variant-selector.js
    return;
  }

  getAvailableOptionsForVariants(variants, currentSelection) {
    // This method is now handled by bundle-variant-selector.js
    return {};
  }

  generateSlotHTML(productData = null) {
    if (!productData) {
      // Empty slot HTML
      return `
      <div class="card card--standard card--media" style="--ratio-percent: 100%">
        <div class="card__inner color-background-2 gradient ratio cursor-pointer hover:bg-gray-50 mb-[1.6rem]" style="--ratio-percent: 100%" onclick="bundleManager.openModal()">
          <div class="card__media">
            <div class="media media--transparent media--hover-effect h-full !static">
              <div class="bg-gray-100 h-full w-full flex items-center justify-center">
                <svg class="w-16 h-16 text-gray-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div class="card__content">
          <div class="quick-add no-js-hidden w-full mt-5">
            <button type="button" 
              class="quick-add__submit button button--full-width button--secondary"
              onclick="bundleManager.openModal()"
            >
              Produkt wählen
            </button>
          </div>
        </div>
      </div>
    `;
    }

    // Check if prices should be hidden
    const bundleSelector = document.querySelector('.bundle-selector');
    const hidePrices = bundleSelector ? bundleSelector.dataset.hidePrices === 'true' : false;
    const priceVisibilityClass = hidePrices ? 'invisible' : '';

    // Product slot HTML
    return `
    <div class="card card--standard card--media" style="--ratio-percent: 100%">
      <div class="card__inner color-background-2 gradient ratio mb-[1.6rem]" style="--ratio-percent: 100%">
        <div class="card__media">
          <div class="media media--transparent media--hover-effect !static">
            <img 
              src="${productData.imageUrl}"
              alt="${productData.title}"
              class="motion-reduce"
              loading="lazy"
              style="--ratio-percent: 100%"
            >
          </div>
        </div>
      </div>
      <div class="card__content">
        <div class="card__information">
          <h3 class="card__heading h5">
            <span class="full-unstyled-link">${productData.title}</span>
          </h3>
          ${productData.variantTitle ? `<p class="text-sm text-gray-600 mt-1">${productData.variantTitle}</p>` : ''}
          <div class="card-information">
            <div class="price ${priceVisibilityClass}">
              <div class="price__container">
                <div class="price__regular">
                  <span class="price-item price-item--regular">${this.formatMoney(productData.price)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="quick-add no-js-hidden w-full mt-5">
          <button type="button" 
            class="quick-add__submit button button--full-width button--secondary"
            onclick="bundleManager.removeProduct('${productData.id}')"
          >
            Entfernen
          </button>
        </div>
      </div>
    </div>
  `;
  }

  addProductToGrid(productData) {
    const grid = document.getElementById('bundleSelectionGrid');
    const emptySlot = grid.querySelector('.bundle-slot');

    if (emptySlot) {
      emptySlot.className = 'bundle-product-slot card-wrapper product-card-wrapper underline-links-hover flex-shrink-0';
      emptySlot.innerHTML = this.generateSlotHTML(productData);
      emptySlot.dataset.productId = productData.id;
    }

    this.selectedProducts.set(productData.id, productData);
    this.validateSelection();
    this.updateSelectedCount();
    this.updateCartPreview();
  }

  deselectProduct(productElement) {
    const productId = productElement.dataset.productId;

    // Einfach das Produkt aus der Auswahl entfernen
    if (this.selectedProducts.has(productId)) {
      this.selectedProducts.delete(productId);

      // Update UI
      this.updateProductVisualState(productElement, false);
      this.updateModalState();
      this.updateGridFromState();
      this.validateSelection();
      this.updateSelectedCount();
      this.updateCartPreview();
    }
  }

  updateSelection() {
    // Aktualisieren Sie hier Ihre UI, z.B. den Gesamtpreis
    this.updateSelectedCount();
  }

  setupVariantEventDelegation() {
    // Handle variant selector button clicks
    // Use VariantSelector instance if available, otherwise fall back to manual toggle
    document.addEventListener('click', (e) => {
      const variantSelectorBtn = e.target.closest('.variant-selector-btn');
      if (variantSelectorBtn) {
        // Try to use VariantSelector instance if available
        const productCard = variantSelectorBtn.closest('.product-card');
        const variantContainer = productCard?.querySelector('.product-variants');
        if (variantContainer?.variantSelector) {
          // Use the VariantSelector instance method which handles everything properly
          e.preventDefault();
          e.stopPropagation();
          variantContainer.variantSelector.toggleVariantDropdown();
          return;
        }

        // Fallback: manual toggle if no VariantSelector instance exists
        e.preventDefault();
        e.stopPropagation();

        const dropdownId = variantSelectorBtn.getAttribute('aria-controls');
        const dropdown = document.getElementById(dropdownId);

        if (dropdown) {
          // Toggle dropdown
          dropdown.classList.toggle('hidden');
          variantSelectorBtn.setAttribute('aria-expanded', !dropdown.classList.contains('hidden'));

          // Close other dropdowns
          document.querySelectorAll('.variant-dropdown').forEach((otherDropdown) => {
            if (otherDropdown !== dropdown) {
              otherDropdown.classList.add('hidden');
              const otherBtn = document.querySelector(`[aria-controls="${otherDropdown.id}"]`);
              if (otherBtn) {
                otherBtn.setAttribute('aria-expanded', 'false');
              }
            }
          });
        }
      }
    });

    // Handle variant option button clicks
    document.addEventListener('click', (e) => {
      const variantOptionBtn = e.target.closest('.variant-option-btn');
      if (variantOptionBtn) {
        e.preventDefault();
        e.stopPropagation();

        const productCard = variantOptionBtn.closest('.product-card');
        const variantContainer = productCard?.querySelector('.product-variants');

        if (variantContainer) {
          // Get all variant options for this option name
          const optionName = variantOptionBtn.dataset.optionName;
          const optionValue = variantOptionBtn.dataset.optionValue;

          // Update selected state for all options of this type
          variantContainer.querySelectorAll(`.variant-option-btn[data-option-name="${optionName}"]`).forEach((btn) => {
            if (btn === variantOptionBtn) {
              btn.classList.add('border-black', 'bg-black', 'text-white');
              btn.classList.remove('border-gray-200', 'bg-white', 'text-black');
              btn.dataset.selected = 'true';
            } else {
              btn.classList.remove('border-black', 'bg-black', 'text-white');
              btn.classList.add('border-gray-200', 'bg-white', 'text-black');
              btn.dataset.selected = 'false';
            }
          });

          // Update variant selector button text
          const variantSelectorBtn = variantContainer.querySelector('.variant-selector-btn');
          if (variantSelectorBtn) {
            variantSelectorBtn.textContent = optionValue;
          }

          // Close dropdown
          const dropdown = variantContainer.querySelector('.variant-dropdown');
          if (dropdown) {
            dropdown.classList.add('hidden');
            variantSelectorBtn.setAttribute('aria-expanded', 'false');
          }

          // Dispatch variant changed event
          const event = new CustomEvent('variant:changed', {
            detail: {
              variantId: variantOptionBtn.dataset.variantId,
              price: variantOptionBtn.dataset.price,
              comparePrice: variantOptionBtn.dataset.comparePrice,
              image: variantOptionBtn.dataset.variantImage,
              hoverImage: variantOptionBtn.dataset.variantHoverImage,
              variantTitle: optionValue,
              sku: variantOptionBtn.dataset.sku,
              selectedColor: variantOptionBtn.dataset.selectedColor,
            },
          });
          productCard.dispatchEvent(event);
        }
      }
    });

    // Handle color variant button clicks
    document.addEventListener('click', (e) => {
      const colorVariantBtn = e.target.closest('.color-variant-btn');
      if (colorVariantBtn) {
        e.preventDefault();
        e.stopPropagation();

        const productCard = colorVariantBtn.closest('.product-card');
        const variantContainer = productCard?.querySelector('.product-variants');

        if (variantContainer) {
          // Initialize variant selector if it doesn't exist
          if (!variantContainer.variantSelector) {
            variantContainer.variantSelector = new VariantSelector(variantContainer);
          }

          // Now we can safely call handleColorVariantClick
          variantContainer.variantSelector.handleColorVariantClick(e);
        }
      }
    });

    // Handle clicks outside dropdowns
    document.addEventListener('click', (e) => {
      const dropdown = e.target.closest('.variant-dropdown');
      const variantSelectorBtn = e.target.closest('.variant-selector-btn');

      if (!dropdown && !variantSelectorBtn) {
        document.querySelectorAll('.variant-dropdown').forEach((dropdown) => {
          dropdown.classList.add('hidden');
          const btn = document.querySelector(`[aria-controls="${dropdown.id}"]`);
          if (btn) {
            btn.setAttribute('aria-expanded', 'false');
          }
        });
      }
    });
  }

  updateMaxProductsDisplay() {
    // Update all maxProductCount elements
    document.querySelectorAll('#maxProductCount').forEach((element) => {
      element.textContent = this.maxProducts;
    });
    // Update CSS variable for progress bar
    document.documentElement.style.setProperty('--max-products', this.maxProducts);
  }
}

window.BundleManager = BundleManager;

// Initialize bundle manager and make it globally available
(function () {
  function initBundleManager() {
    if (!window.bundleManager) {
      window.bundleManager = new BundleManager();
    }
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    initBundleManager();
  } else {
    document.addEventListener('DOMContentLoaded', initBundleManager);
  }
})();

}
